/**
 * TemplateSelector - 字幕模板选择器组件
 *
 * 功能：展示预设字幕模板，用户可点击选择
 * 特点：独立组件，集成 LivePreview 展示效果
 */

import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { getSubtitleTemplates } from '../../utils/api';
import type { SubtitleTemplate } from '../../types';
import LivePreview from './LivePreview';

interface TemplateSelectorProps {
  /** 当前选中的模板ID */
  selectedTemplateId: string;
  /** 选中模板回调 */
  onSelectTemplate: (template: SubtitleTemplate) => void;
  /** 是否显示完整预览（大卡片）或迷你预览（小卡片） */
  previewMode?: 'full' | 'mini';
  /** 翻译函数 */
  t?: (key: string) => string;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplateId,
  onSelectTemplate,
  previewMode = 'mini',
  t = (key: string) => key
}) => {
  const [templates, setTemplates] = useState<SubtitleTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  // 加载模板列表
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        const response = await getSubtitleTemplates();
        setTemplates(response.templates || []);
      } catch (error) {
        console.error('加载字幕模板失败:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="template-selector">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {templates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            isSelected={selectedTemplateId === template.id}
            onSelect={() => onSelectTemplate(template)}
            previewMode={previewMode}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

interface TemplateCardProps {
  template: SubtitleTemplate;
  isSelected: boolean;
  onSelect: () => void;
  previewMode: 'full' | 'mini';
  /** 翻译函数 */
  t?: (key: string) => string;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  isSelected,
  onSelect,
  previewMode,
  t = (key: string) => key
}) => {
  // 计算预览尺寸：容器750px - padding(32px) - gaps(24px) = 694px / 3 ≈ 231px/列
  // 减去卡片内边距约30px，预览区约200px
  const previewWidth = previewMode === 'mini' ? 190 : 250;

  return (
    <div
      className={`template-card relative cursor-pointer rounded-lg border-2 transition-all duration-200 w-full ${
        isSelected
          ? 'border-blue-500 shadow-lg scale-105'
          : 'border-gray-600 hover:border-gray-500 hover:shadow-md'
      }`}
      onClick={onSelect}
    >
      {/* 选中标记 */}
      {isSelected && (
        <div className="absolute top-2 right-2 z-10 bg-blue-500 rounded-full p-1">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}

      {/* 模板名称 */}
      <div className="p-3 pb-2">
        <div className="text-sm font-medium text-white">{template.name}</div>
      </div>

      {/* CSS 预览 */}
      <div className="px-3 pb-3 flex justify-center">
        <LivePreview
          style={template.style}
          text={t('subtitle.templatePreview')}
          width={previewWidth}
          height={previewWidth * 0.6}
        />
      </div>
    </div>
  );
};

export default TemplateSelector;
